import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform, View } from 'react-native';
import {
  COLOR,
  FONT_SIZE,
  FONT_WEIGHT,
  RADIUS,
  SPACING,
} from 'constants/design-system';
import { DateTime } from 'luxon';

import { OrderItems } from 'modules/GridView/components/OrderDetails/OrderItems';
import { useAppSelector } from 'store/hooks';
import { OrderPreparationStatuses, OrderStatuses } from 'store/Orders/enums';
import { SettingSelectors } from 'store/Settings';
import { UserSelectors } from 'store/User';
import { useFormatDateTime } from 'util/helpers';
import { BaseButton } from 'components/BaseButton';
import { BaseCard } from 'components/BaseCard';
import { BaseIcon } from 'components/BaseIcon';
import { BasePressable } from 'components/BasePressable';
import { BaseText } from 'components/BaseText';
import CancelledLabel from 'components/CancelledLabel';

import { Header } from './components/Header';
import type { BaseButtonProps } from 'components/BaseButton';
import type { BaseCardProps } from 'components/BaseCard';
import type { BaseIconProps } from 'components/BaseIcon';
import type { GestureResponderEvent } from 'react-native';
import type { Order, OrderItem } from 'store/Orders/types';
import type { DiscriminatedPick } from 'util/types/discriminated-pick';

export type OrderDetailCardForGridProps = Pick<BaseCardProps, 'style'> & {
  order: Order;
  items: OrderItem[];
  onPress?: (event: GestureResponderEvent, orderUuid: string) => void;
  onPressOrderItem?: (itemUuid: string) => void;
  hiddenItemsCount: number;
  onPressShowHiddenItem?: BaseButtonProps['onPress'];
  shouldIncludeGhostMarginForScheduledForHead?: boolean;
};

export const OrderDetailCardForGrid = (props: OrderDetailCardForGridProps) => {
  const {
    items,
    order,
    style,
    onPressShowHiddenItem,
    onPress,
    onPressOrderItem,
    hiddenItemsCount,
    shouldIncludeGhostMarginForScheduledForHead,
  } = props;

  const formatDateTime = useFormatDateTime();

  // const orderInternalStatus: OrderInternalStatuses = useMemo(() => {
  //   return getOrderInternalStatus(order, items);
  // }, [order, items]);

  const { t } = useTranslation();

  const hasInternalNote =
    order.internal_notes && order.internal_notes.length > 0;
  const hasSpecialInstruction = order.special_instructions;
  const shouldShowDivider = hasInternalNote && hasSpecialInstruction;

  const isCancelled =
    order.status === OrderPreparationStatuses.CANCELLED ||
    order.order_status === OrderStatuses.CANCELLED;

  const cardViewLayoutCollapsibility = useAppSelector(
    SettingSelectors.selectCardViewLayoutCollapsibility,
  );

  const [isCollapse, setIsCollapse] = useState(false);

  const isGridViewCollapse = useAppSelector(
    SettingSelectors.selectIsGridViewCollapsed,
  );

  useEffect(() => {
    if (isGridViewCollapse !== undefined) {
      setIsCollapse(isGridViewCollapse);
    }
  }, [isGridViewCollapse]);

  const featureFlagInterfaceSettings = useAppSelector(
    UserSelectors.selectInterfaceSettings,
  );
  const collapsibleCardsItemThreshold =
    featureFlagInterfaceSettings.collapsible_cards_item_threshold;
  const enableCollapsibleCards =
    featureFlagInterfaceSettings.enable_collapsible_cards;

  const hasCollapseItem = items.length - collapsibleCardsItemThreshold > 0;

  return (
    <View>
      <BasePressable
        onPress={(e) => {
          onPress?.(e, order.uuid);
        }}
        style={style}
      >
        {order.scheduled_for ? (
          <View
            style={{
              height: 32,
              justifyContent: 'center',
              backgroundColor: COLOR.theme.blue[400],
              marginHorizontal: SPACING.theme[10],
              borderTopLeftRadius: RADIUS.theme.md,
              borderTopRightRadius: RADIUS.theme.md,
              padding: SPACING.theme[2],
            }}
          >
            <BaseText
              numberOfLines={1}
              ellipsizeMode="head"
              color={COLOR.theme.white}
              textAlign="center"
              fontWeight={FONT_WEIGHT.theme.bold}
              fontSize={FONT_SIZE.theme.sm}
            >
              {`${t('overview.orderDetails.scheduledFor').toUpperCase()} ${formatDateTime(order.scheduled_for, DateTime.TIME_SIMPLE)} - ${formatDateTime(
                order.scheduled_for,
                {
                  month: 'short',
                  day: 'numeric',
                },
              )}`}
            </BaseText>
          </View>
        ) : shouldIncludeGhostMarginForScheduledForHead ? (
          <View style={{ marginTop: 32 }} />
        ) : null}
        <BaseCard
          style={[
            {
              paddingHorizontal: SPACING.theme[0],
              backgroundColor: isCancelled
                ? COLOR.theme.gray[500]
                : COLOR.system.orderKinds[order.kind],
              gap: SPACING.theme[4],
              flex: 1,
              paddingBottom: 0,
            },
            order.scheduled_for
              ? {
                  borderWidth: 3,
                  borderColor: COLOR.theme.blue[400],
                }
              : false,
          ]}
        >
          <View
            style={{
              flex: 1,
            }}
          >
            <Header
              selectedOrderItemUuid={undefined}
              style={{ paddingHorizontal: SPACING.theme[8] }}
              order={order}
              items={items}
            />
            <View
              style={{
                backgroundColor: COLOR.theme.white,
                marginTop: SPACING.theme[8],
              }}
            >
              {hasSpecialInstruction && (
                <View
                  style={{
                    flexDirection: 'row',
                    gap: SPACING.theme[4],
                    padding: SPACING.theme[4],
                    alignItems: 'center',
                  }}
                >
                  <BaseIcon
                    variant="material-community"
                    name="account-details"
                    color={COLOR.theme.red[500]}
                    size={FONT_SIZE.theme['2xl']}
                  />

                  <BaseText
                    fontSize={FONT_SIZE.theme.sm}
                    color={COLOR.theme.red[500]}
                    numberOfLines={2}
                    fontWeight={FONT_WEIGHT.theme.medium}
                  >
                    {order.special_instructions}
                  </BaseText>
                </View>
              )}
              {shouldShowDivider && (
                <View
                  style={{
                    borderTopWidth: 1,
                    borderColor: COLOR.theme.gray[300],
                    marginHorizontal: SPACING.theme[4],
                  }}
                />
              )}
              {hasInternalNote && (
                <View
                  style={{
                    flexDirection: 'row',
                    gap: SPACING.theme[4],
                    padding: SPACING.theme[4],
                    alignItems: 'center',
                  }}
                >
                  <BaseIcon
                    variant="material"
                    name="headset-mic"
                    size={FONT_SIZE.theme['2xl']}
                  />

                  <BaseText
                    fontWeight={FONT_WEIGHT.theme.medium}
                    fontSize={FONT_SIZE.theme.sm}
                    numberOfLines={2}
                  >
                    {order.internal_notes
                      ?.map((internalNote) => internalNote.note)
                      .join('; ')}
                  </BaseText>
                </View>
              )}

              <OrderItems
                actionButtonArrangement="vertical"
                shouldPizzaAddonVerticalOnly
                disabledScroll
                order={order}
                items={items}
                onPressItem={onPressOrderItem}
                maxNumberOfItemShownInList={
                  isCollapse &&
                  enableCollapsibleCards &&
                  cardViewLayoutCollapsibility
                    ? collapsibleCardsItemThreshold
                    : undefined
                }
              />
            </View>
          </View>
          {isCancelled && <CancelledLabel />}
        </BaseCard>
      </BasePressable>
      <View
        style={{ flexDirection: 'row', marginHorizontal: SPACING.theme[8] }}
      >
        {(isCollapse === false ||
          !hasCollapseItem ||
          !enableCollapsibleCards) &&
          hiddenItemsCount > 0 && (
            <CustomButton
              title={t('overview.orderDetails.hiddenItems', {
                count: hiddenItemsCount,
              })}
              onPress={onPressShowHiddenItem}
              startIcon={{
                variant: 'material-community',
                name: 'eye-off',
                color: '#0073D0',
              }}
              color="#0073D0"
            />
          )}
        {cardViewLayoutCollapsibility &&
          hasCollapseItem &&
          enableCollapsibleCards && (
            <CustomButton
              title={t(
                isCollapse
                  ? 'overview.orderDetails.expandCard'
                  : 'overview.orderDetails.collapseCard',
                {
                  count: items.length - collapsibleCardsItemThreshold,
                },
              )}
              titleAltCount={items.length - collapsibleCardsItemThreshold}
              startIcon={{
                name: isCollapse
                  ? 'arrow-expand-vertical'
                  : 'arrow-collapse-up',
                variant: 'material-community',
                color: isCollapse ? '#FF3232' : '#000',
              }}
              style={[!isCollapse && { backgroundColor: '#FFF' }]}
              color={isCollapse ? '#FF3232' : '#000'}
              onPress={() => {
                setIsCollapse((prev) => !prev);
              }}
            />
          )}
      </View>
    </View>
  );
};

export type CustomButtonProps = {
  title?: BaseButtonProps['title'];
  style?: BaseButtonProps['style'];
  onPress?: BaseButtonProps['onPress'];
  startIcon?: DiscriminatedPick<
    Exclude<BaseButtonProps['startIcon'], undefined>,
    'name' | 'variant' | 'color'
  >;
  color?: BaseButtonProps['color'];
  titleAltCount?: BaseButtonProps['title'];
};
export const CustomButton = (props: CustomButtonProps) => {
  const { title, color, style, onPress, startIcon, titleAltCount } = props;
  const [isOverflowing, setIsOverflowing] = useState(false);

  return (
    <BaseButton
      onLayout={(event) => {
        const { layout } = event.nativeEvent;

        const width = layout.width;
        if (width < 150) {
          setIsOverflowing(true);
        } else {
          setIsOverflowing(false);
        }
      }}
      title={isOverflowing ? titleAltCount : title}
      onPress={onPress}
      startIcon={
        startIcon
          ? {
              ...startIcon,
              size: FONT_SIZE.theme.xl,
            }
          : undefined
      }
      color={color}
      fontSize={FONT_SIZE.theme.xs}
      fontWeight={FONT_WEIGHT.theme.medium}
      backgroundColor="#E6E6E6"
      numberOfLines={1}
      ellipsizeMode="head"
      textStyle={[
        Platform.OS === 'web' && {
          direction: 'rtl',
        },
      ]}
      style={({ pressed, ...rest }) => [
        {
          flex: 1,
          borderTopLeftRadius: 0,
          borderTopRightRadius: 0,
          borderColor: '#9B9B9B',
          borderWidth: 1,
        },
        typeof style === 'function' ? style({ pressed, ...rest }) : style,
      ]}
    />
  );
};
